import React, { useEffect, useRef, useState } from "react";
import { FiSend, FiMic, FiSquare, FiRotateCcw, FiCheck } from "react-icons/fi";
import Answer from "../chatbot/Answer";
import { API_BASE_URL } from "../../api";
import { askChatbot, recordingToWav } from "../../chatbot/chat";
import { toWhatsApp, waFormat } from "../../chatbot/whatsapp";

const SUGGESTIONS = ["What can you help me with?", "Explain what a VPN is in one sentence", "ہیلو، آپ کیا کر سکتے ہیں؟"];
const clock = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function WaText({ text }) {
  return waFormat(toWhatsApp(text)).map((p, i) => (p.kind === "bold" ? <strong key={i}>{p.text}</strong> : p.kind === "italic" ? <em key={i}>{p.text}</em> : p.kind === "strike" ? <s key={i}>{p.text}</s> : p.kind === "code" ? <code key={i} className="font-mono text-[0.9em]">{p.text}</code> : <React.Fragment key={i}>{p.text}</React.Fragment>));
}

// One assistant, two doors: the same conversation shows in a website chat window and on a WhatsApp phone. A question asked on either side is remembered on the other, like a customer who starts on the website and goes on in WhatsApp.
export default function UnifiedDemo() {
  const [demo, setDemo] = useState(undefined);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [inputs, setInputs] = useState({ web: "", wa: "" });
  const conversation = useRef(null);
  const abort = useRef(null);
  const recorder = useRef(null);
  const logs = { web: useRef(null), wa: useRef(null) };

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/core/chatbot/demo/`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive) setDemo(d); }).catch(() => { if (alive) setDemo(null); });
    return () => { alive = false; };
  }, []);
  useEffect(() => { ["web", "wa"].forEach((k) => { if (logs[k].current) logs[k].current.scrollTop = logs[k].current.scrollHeight; }); }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (abort.current) abort.current.abort(); if (recorder.current && recorder.current.state === "recording") recorder.current.stop(); }, []);

  const ask = async (from, text, audio) => {
    if (busy || !demo) return;
    setBusy(true);
    const id = Date.now();
    const now = new Date();
    setMessages((m) => [...m, { id, role: "user", from, text: text || "", voice: !!audio, at: now }, { id: id + 1, role: "assistant", from, text: "", pending: true, at: now }]);
    const patch = (which, change) => setMessages((m) => m.map((x) => (x.id === which ? { ...x, ...change } : x)));
    const controller = new AbortController();
    abort.current = controller;
    let reply = "";
    try {
      let body;
      if (audio) {
        body = new FormData();
        body.append("audio", audio, "voice-note.wav");
        if (conversation.current) body.append("conversation_id", conversation.current);
      } else {
        body = { message: text, conversation_id: conversation.current || undefined };
      }
      await askChatbot({
        widgetKey: demo.widget_key, body, signal: controller.signal,
        onEvent: (e) => {
          if (e.conversation_id) conversation.current = e.conversation_id;
          if (e.transcript) patch(id, { text: e.transcript });
          if (e.delta) { reply += e.delta; patch(id + 1, { text: reply, pending: false }); }
          if (e.done) patch(id + 1, { text: e.reply, pending: false, at: new Date() });
          if (e.error) { patch(id + 1, { text: e.error.message, pending: false, error: true }); if (audio) patch(id, { text: "(voice note)" }); }
        },
      });
    } catch (err) {
      if (!err || err.name !== "AbortError") patch(id + 1, { text: "Something went wrong. Please try again.", pending: false, error: true });
    } finally {
      setBusy(false);
    }
  };

  const submit = (from) => (e) => { e.preventDefault(); const t = inputs[from].trim(); if (!t) return; setInputs((i) => ({ ...i, [from]: "" })); ask(from, t); };

  const toggleVoice = async () => {
    if (recording && recorder.current) { recorder.current.stop(); return; }
    if (busy || !navigator.mediaDevices || !window.MediaRecorder) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const rec = new window.MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        try { ask("wa", "", await recordingToWav(new Blob(chunks, { type: rec.mimeType }))); } catch { /* an unreadable recording is ignored */ }
      };
      recorder.current = rec;
      setRecording(true);
      rec.start();
      setTimeout(() => { if (rec.state === "recording") rec.stop(); }, 30000);
    } catch { setRecording(false); }
  };

  const reset = () => { if (abort.current) abort.current.abort(); conversation.current = null; setMessages([]); setBusy(false); };
  const canVoice = typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof window !== "undefined" && !!window.MediaRecorder;

  if (demo === undefined) return <div className="rounded-2xl bg-white/5 border border-navy-border h-[560px] flex items-center justify-center text-gray-400">Loading the demo...</div>;
  if (demo === null) return <div className="rounded-2xl bg-white/5 border border-navy-border h-[240px] flex items-center justify-center text-gray-400 px-6 text-center">The live demo is not switched on yet.</div>;

  return (
    <div>
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div className="rounded-2xl bg-white/5 border border-navy-border flex flex-col h-[560px]">
          <div className="px-4 py-3 border-b border-navy-border"><div className="font-semibold">On your website</div><div className="text-[11px] text-gray-400">{demo.name} - the chat window</div></div>
          <div ref={logs.web} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" role="log" aria-label="Website chat">
            <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white/10 px-4 py-2.5 text-sm">{demo.greeting}</div>
            {!messages.length && <div className="flex flex-wrap gap-2">{SUGGESTIONS.map((s) => <button key={s} type="button" disabled={busy} onClick={() => ask("web", s)} dir="auto" className="text-xs px-3 py-1.5 rounded-full border border-navy-border text-gray-300 hover:border-accent-light disabled:opacity-40">{s}</button>)}</div>}
            {messages.map((m) => (
              <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className="max-w-[88%]">
                  <div className={`px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-brand-gradient" : m.error ? "rounded-2xl rounded-bl-sm bg-red-500/15 border border-red-500/30 text-red-100" : "rounded-2xl rounded-bl-sm bg-white/10"}`}>
                    {m.pending && !m.text ? "..." : m.role === "user" && m.voice && !m.text ? "Voice note..." : <Answer text={m.text} />}
                  </div>
                  {m.role === "user" && m.from === "wa" && <div className="text-[10px] text-emerald-300 mt-0.5 text-right">sent on WhatsApp</div>}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={submit("web")} className="p-3 border-t border-navy-border flex gap-2">
            <input value={inputs.web} onChange={(e) => setInputs((i) => ({ ...i, web: e.target.value }))} dir="auto" aria-label="Message on the website" placeholder="Type on the website..." className="flex-1 bg-navy border border-navy-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-accent-light" />
            <button type="submit" disabled={busy || !inputs.web.trim()} aria-label="Send on the website" className="p-2.5 rounded-xl bg-brand-gradient disabled:opacity-40"><FiSend /></button>
          </form>
        </div>

        <div className="mx-auto w-full max-w-[340px]">
          <div className="rounded-[34px] border-[10px] border-[#0b141a] bg-[#0b141a] shadow-2xl shadow-black/50 overflow-hidden h-[560px] flex flex-col">
            <div className="bg-[#202c33] px-4 py-2.5 flex items-center gap-3 text-white">
              <span className="w-9 h-9 rounded-full bg-[#25d366] flex items-center justify-center text-sm font-bold text-[#0b141a]">{(demo.name || "A").slice(0, 1)}</span>
              <div className="min-w-0"><div className="text-sm font-semibold truncate">{demo.name}</div><div className="text-[11px] text-[#8696a0]">{busy ? "typing..." : "online"}</div></div>
            </div>
            <div ref={logs.wa} className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5 bg-[#0b141a]" role="log" aria-label="WhatsApp chat" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "18px 18px" }}>
              {messages.length === 0 && <div className="mx-auto text-[11px] text-[#8696a0] bg-[#182229] rounded-lg px-3 py-1.5 w-fit">Messages are answered by the AI assistant</div>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] rounded-lg px-2.5 py-1.5 text-[13.5px] leading-snug text-[#e9edef] ${m.role === "user" ? "bg-[#005c4b] rounded-tr-none" : m.error ? "bg-red-900/50 rounded-tl-none" : "bg-[#202c33] rounded-tl-none"}`}>
                    {m.pending && !m.text ? <span className="text-[#8696a0]">typing...</span> : m.role === "user" && m.voice && !m.text ? <span className="flex items-center gap-1.5"><FiMic /> Voice note</span> : (
                      <span dir="auto" className="whitespace-pre-wrap break-words">{m.voice && m.role === "user" && <FiMic className="inline mr-1 text-[#53bdeb]" />}<WaText text={m.text} /></span>
                    )}
                    <span className="float-right ml-2 mt-1 text-[10px] text-[#8696a0] inline-flex items-center gap-0.5">{clock(m.at)}{m.role === "user" && <FiCheck className="text-[#53bdeb]" />}</span>
                  </div>
                  {m.role === "user" && m.from === "web" && <span className="self-end ml-1 text-[9px] text-[#8696a0] hidden">website</span>}
                </div>
              ))}
            </div>
            <form onSubmit={submit("wa")} className="bg-[#202c33] p-2 flex items-center gap-2">
              <input value={inputs.wa} onChange={(e) => setInputs((i) => ({ ...i, wa: e.target.value }))} dir="auto" aria-label="Message on WhatsApp" placeholder="Message" className="flex-1 rounded-full bg-[#2a3942] text-[#e9edef] placeholder-[#8696a0] px-4 py-2 text-sm outline-none" />
              {inputs.wa.trim() || !canVoice ? (
                <button type="submit" disabled={busy || !inputs.wa.trim()} aria-label="Send on WhatsApp" className="w-10 h-10 rounded-full bg-[#00a884] text-white flex items-center justify-center disabled:opacity-40"><FiSend /></button>
              ) : (
                <button type="button" onClick={toggleVoice} disabled={busy && !recording} aria-label={recording ? "Stop and send the voice note" : "Record a voice note"} aria-pressed={recording} className={`w-10 h-10 rounded-full text-white flex items-center justify-center disabled:opacity-40 ${recording ? "bg-red-500 animate-pulse" : "bg-[#00a884]"}`}>{recording ? <FiSquare /> : <FiMic />}</button>
              )}
            </form>
          </div>
          <p className="text-[11px] text-gray-500 text-center mt-3">A mock-up of WhatsApp. On a real phone the same assistant answers your customers.</p>
        </div>
      </div>
      <div className="flex justify-center mt-5"><button type="button" onClick={reset} className="text-xs px-4 py-2 rounded-full border border-navy-border text-gray-300 hover:border-accent-light inline-flex items-center gap-2"><FiRotateCcw /> Start a new conversation</button></div>
    </div>
  );
}
